import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  bulkDeleteCustomers,
  createCustomer,
  deleteCustomer,
  getCustomerById,
  getCustomersList,
  getCustomersStats,
  patchCustomerStatus,
  updateCustomer,
  type CustomerListParams,
  type CustomerStatusAction,
} from "src/services/customerService";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import { QueryNames } from "./queries.constant";
import { useGeneralRefetchOverride } from "./general.queries";

export const useQueryGetCustomersList = (
  params: CustomerListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const customQueryKey = [...QueryNames.customerList, params, language];

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: customQueryKey,
      queryFn: () => getCustomersList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetCustomersStats = (
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.customerStats, params, language],
      queryFn: () => getCustomersStats(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useMutationDeleteCustomer = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteCustomer(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.customerList });
      queryClient.invalidateQueries({ queryKey: QueryNames.customerStats });
    },
  });
};

export const useQueryGetCustomerById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.customerList, "detail", id, language],
      queryFn: () => getCustomerById(id as string | number, language),
      enabled: !!id && options.enabled !== false,
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    !!id && options.enabled !== false,
  );
};

export const useMutationCreateCustomer = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      createCustomer(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.customerList });
      queryClient.invalidateQueries({ queryKey: QueryNames.customerStats });
    },
  });
};

export const useMutationUpdateCustomer = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Record<string, unknown>;
    }) => updateCustomer(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.customerList });
      queryClient.invalidateQueries({ queryKey: QueryNames.customerStats });
    },
  });
};

export const useMutationPatchCustomerStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string | number;
      status: CustomerStatusAction;
    }) => patchCustomerStatus(id, status, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.customerList });
      queryClient.invalidateQueries({ queryKey: QueryNames.customerStats });
    },
  });
};

export const useMutationBulkDeleteCustomers = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (ids: Array<string | number>) =>
      bulkDeleteCustomers(ids, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.customerList });
      queryClient.invalidateQueries({ queryKey: QueryNames.customerStats });
    },
  });
};
